import { Temporal } from 'temporal-polyfill';

import {
  DateInputError,
  formatIsoUtc,
  formatLocalIso,
  formatSpreadsheet,
  formatUnixMilliseconds,
  formatUnixSeconds,
  getZoneStatus,
  parseIsoInstant,
  validateTimeZone,
} from './date-engine.js';
import { DIALECTS, formatCustom } from './custom-format.js';
import {
  formatSourceInstant,
  parseDateInput,
} from './input-parser.js';
import { buildRowLink } from './url-state.js';
import './styles.css';

const STORAGE = Object.freeze({
  zone: 'time.brent.app:zone',
  order: 'time.brent.app:date-order',
  dialect: 'time.brent.app:custom-dialect',
  pattern: 'time.brent.app:custom-pattern',
});

const FIELD_DEFINITIONS = Object.freeze([
  { id: 'unix-seconds', label: 'Unix seconds', short: 'seconds' },
  { id: 'unix-milliseconds', label: 'Unix milliseconds', short: 'milliseconds' },
  { id: 'spreadsheet', label: 'Spreadsheet', short: '1900 system · selected-zone wall time' },
  { id: 'iso', label: 'ISO 8601', short: 'UTC · Z' },
  { id: 'local', label: 'Local', short: 'selected zone · numeric offset' },
  { id: 'custom', label: 'Custom', short: 'Day.js / Moment' },
]);

const DEFAULT_DIALECT = 'moment';
const DEFAULT_PATTERN = 'YYYY-MM-DD HH:mm:ss.SSS Z';
const FALLBACK_ZONES = Object.freeze([
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Kyiv',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Kathmandu',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Lord_Howe',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Pacific/Apia',
]);

function nowInstant() {
  return Temporal.Instant.fromEpochMilliseconds(Date.now());
}

function safeStorageGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private browsing or a storage policy can disable localStorage. The app
    // remains fully functional for the current visit.
  }
}

function browserTimeZone() {
  try {
    return validateTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  } catch {
    return 'UTC';
  }
}

function localeDateOrder() {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      timeZone: 'UTC',
    }).formatToParts(new Date(Date.UTC(2006, 10, 22)));
    const monthIndex = parts.findIndex((part) => part.type === 'month');
    const dayIndex = parts.findIndex((part) => part.type === 'day');
    return dayIndex < monthIndex ? 'DMY' : 'MDY';
  } catch {
    return 'MDY';
  }
}

function queryTimeZone(params, fallback) {
  const requested = params.get('tz');
  for (const candidate of [requested, fallback, browserTimeZone(), 'UTC']) {
    if (!candidate) continue;
    try {
      return validateTimeZone(candidate);
    } catch {
      // Continue to a safe IANA fallback.
    }
  }
  return 'UTC';
}

function queryDateOrder(params) {
  const requested = params.get('order');
  if (requested === 'MDY' || requested === 'DMY') return requested;
  const stored = safeStorageGet(STORAGE.order);
  if (stored === 'MDY' || stored === 'DMY') return stored;
  return localeDateOrder();
}

function queryDialect(params) {
  const candidate = params.get('dialect') || safeStorageGet(STORAGE.dialect) || DEFAULT_DIALECT;
  return Object.hasOwn(DIALECTS, candidate) ? candidate : DEFAULT_DIALECT;
}

function queryPattern(params, dialect) {
  return params.get('fmt') || safeStorageGet(STORAGE.pattern) || DIALECTS[dialect].examplePattern;
}

function cloneSource(source) {
  return source ? { ...source } : null;
}

function resultWithSource(result, source) {
  return {
    ...result,
    source: Object.freeze(source),
    provenance: Object.freeze(source),
    format: source.kind,
  };
}

function decodePathVariants(pathname) {
  const encoded = pathname.replace(/^\/+|\/+$/g, '');
  if (!encoded) return [];
  const literal = decodeURIComponent(encoded);
  const variants = [literal];
  if (encoded.includes('+')) {
    const natural = decodeURIComponent(encoded.replaceAll('+', '%20'));
    if (natural !== literal) {
      // ISO offsets are literal; all other raw path plus signs delimit words.
      if (/T\d{2}:\d{2}.*\+\d{2}:?\d{2}$/.test(literal)) variants.push(natural);
      else variants.unshift(natural);
    }
  }
  return variants;
}

function sourceNeedsZone(source) {
  return ['spreadsheet', 'local', 'custom', 'natural'].includes(source?.kind);
}

function encodeNaturalExpression(value) {
  return encodeURIComponent(value)
    .replaceAll('%20', '+')
    .replaceAll('%3A', ':')
    .replaceAll('%2C', ',');
}

function encodeFriendlyValue(value, preserveSlash = false) {
  let encoded = encodeURIComponent(value)
    .replaceAll('%3A', ':')
    .replaceAll('%2C', ',')
    .replaceAll('%2E', '.');
  if (preserveSlash) encoded = encoded.replaceAll('%2F', '/');
  return encoded;
}

function sourcePath(source, { forceExplicit = false } = {}) {
  if (!source || source.kind === 'live') return '/';
  const raw = source.raw || '';

  if (source.kind === 'natural') return `/${encodeNaturalExpression(raw)}`;
  if (source.kind === 'custom') {
    if (forceExplicit || source.explicit) return `/custom/${encodeFriendlyValue(raw, true)}`;
    return `/${encodeFriendlyValue(raw, true)}`;
  }

  const prefix = {
    'unix-seconds': 'unix',
    'unix-milliseconds': 'unix-ms',
    spreadsheet: 'spreadsheet',
    iso: 'iso',
    local: 'iso',
  }[source.kind];
  if ((forceExplicit || source.explicit) && prefix) {
    return `/${prefix}/${encodeFriendlyValue(raw)}`;
  }
  return `/${encodeFriendlyValue(raw)}`;
}

function sourceQuery(source, appState, { includeZone = sourceNeedsZone(source) } = {}) {
  const params = new URLSearchParams();
  if (includeZone) params.set('tz', appState.zone);
  if (source?.kind === 'custom') {
    params.set('dialect', source.dialect || appState.customDialect);
    params.set('fmt', source.pattern || source.fmt || appState.customPattern);
    if (source.dateOrder === 'MDY' || source.dateOrder === 'DMY') {
      params.set('order', source.dateOrder);
    }
  }
  const fold = source?.fold || appState.fold;
  if (fold === 'earlier' || fold === 'later') params.set('fold', fold);
  const query = params.toString();
  return query ? `?${query}` : '';
}

function relativeUrlForSource(source, appState, options) {
  return `${sourcePath(source, options)}${sourceQuery(source, appState, options)}`;
}

function absoluteUrlForSource(source, appState, options) {
  return new URL(relativeUrlForSource(source, appState, options), window.location.origin).href;
}

const ICONS = {
  copy: '<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3"/>',
  link: '<path d="m10 13 4-4m-6 6-1 1a4 4 0 0 1-6-6l4-4a4 4 0 0 1 6 0m2 2 1-1a4 4 0 0 1 6 6l-4 4a4 4 0 0 1-6 0" transform="translate(1 1)"/>',
};
function icon(name) {
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name]}</svg>`;
}

function fieldMarkup(field) {
  return `
    <div class="field-row" data-row="${field.id}">
      <label class="field-label" for="field-${field.id}">
        ${field.label}
        <span class="field-label__meta" id="meta-${field.id}">${field.short}</span>
      </label>
      <div class="well" id="well-${field.id}">
        <input id="field-${field.id}" data-field="${field.id}" inputmode="text" autocomplete="off" autocapitalize="off" spellcheck="false" aria-describedby="feedback">
      </div>
      <div class="row-actions" aria-label="${field.label} actions">
        <button class="skin-button icon-button" type="button" data-copy="${field.id}" aria-label="Copy ${field.label}" title="Copy ${field.label}">${icon('copy')}</button>
        <button class="skin-button icon-button" type="button" data-link="${field.id}" aria-label="Copy a ${field.label} link" title="Copy link to this ${field.label} value">${icon('link')}</button>
      </div>
    </div>`;
}

document.querySelector('#app').innerHTML = `
  <a class="skip-link" href="#converter">Skip to converter</a>
  <main class="site-shell" id="converter" tabindex="-1">
    <section class="instrument" aria-labelledby="site-title">
      <div class="instrument__inner">
        <header class="masthead">
          <div>
            <h1 class="wordmark" id="site-title">time.brent.app</h1>
            <p class="tagline">A short url tool for translating time. e.g. time.brent.app/45270</p>
          </div>
          <div class="zone-status" aria-live="off">
            <button class="zone-status__name" id="zone-name" type="button" title="Change time zone" aria-label="Change time zone"></button>
            <time class="zone-status__clock" id="zone-clock"></time>
          </div>
        </header>

        <div class="primary-block">
          <div class="primary-label">
            <label for="primary-input">Paste a date or try “next friday”</label>
            <button type="button" class="mode-badge" id="mode-toggle" aria-label="Toggle live time"><span class="mode-lamp" id="mode-lamp" aria-hidden="true"></span><span id="mode-label">Live</span></button>
          </div>
          <div class="primary-controls">
            <div class="well primary-well" id="well-primary">
              <input id="primary-input" data-field="primary" inputmode="text" autocomplete="off" autocapitalize="off" spellcheck="false" aria-describedby="feedback" aria-label="Date, timestamp, or English expression">
            </div>
            <div class="primary-actions" aria-label="Primary input actions">
              <button class="skin-button icon-button" type="button" data-copy="primary" aria-label="Copy primary value" title="Copy value">${icon('copy')}</button>
              <button class="skin-button icon-button" type="button" data-link="primary" aria-label="Copy primary value link" title="Copy link">${icon('link')}</button>
            </div>
          </div>
          <output class="date-summary" id="date-summary" aria-live="off"></output>
          <div class="feedback" id="feedback" role="status" aria-live="polite"></div>
          <div class="choice-panel" id="choice-panel" data-open="false" role="group" aria-label="Resolve date ambiguity">
            <span id="choice-copy"></span>
            <div class="choice-actions" id="choice-actions"></div>
          </div>
        </div>

        <div class="field-bank" aria-label="Editable date formats">
          ${FIELD_DEFINITIONS.map(fieldMarkup).join('')}
        </div>

        <footer class="control-deck">
          <p class="instrument-note">Edit any value to convert. <span id="date-order-note"></span></p>
          <div class="deck-actions">
            <button class="skin-button skin-button--wide" id="now-button" type="button">Now</button>
            <button class="skin-button skin-button--wide" id="custom-button" type="button">Custom</button>
            <button class="skin-button skin-button--wide" id="options-button" type="button">Options</button>
          </div>
        </footer>
        <details class="usage-help">
          <summary>Examples &amp; tips</summary>
          <dl>
            <div><dt>Logs &amp; APIs</dt><dd>Paste Unix seconds, milliseconds, or ISO. Copy any result, or use its chain icon to share that exact value.</dd></div>
            <div><dt>Planning ahead</dt><dd>Try <a href="/next+friday">next friday</a> or <a href="/tomorrow+at+4pm">tomorrow at 4pm</a>. Change the timezone above to see the same instant elsewhere.</dd></div>
            <div><dt>Move a date</dt><dd>Replace the top value with <code>+ 1 week</code> or <code>- 90 minutes</code> to shift the displayed time.</dd></div>
            <div><dt>Spreadsheets &amp; custom dates</dt><dd><a href="/46000">46000</a> is December 9, 2025 in Excel / Google Sheets. Serial values use the selected zone; serial 60 is invalid. Custom supports your own date pattern.</dd></div>
          </dl>
        </details>
      </div>
    </section>
  </main>

  <dialog id="options-dialog" aria-labelledby="options-title">
    <h2 class="dialog-title" id="options-title">Instrument options</h2>
    <p class="dialog-copy">See the same moment in another time zone. Local and spreadsheet values update together.</p>
    <div class="form-grid">
      <div class="form-field">
        <label for="zone-input">IANA time zone</label>
        <input id="zone-input" list="zone-list" autocomplete="off" spellcheck="false">
        <datalist id="zone-list"></datalist>
        <span class="form-help" id="browser-zone-help"></span>
      </div>
      <div class="form-field">
        <label for="order-select">Numeric calendar order</label>
        <select id="order-select">
          <option value="MDY">MDY — month / day / year</option>
          <option value="DMY">DMY — day / month / year</option>
        </select>
        <span class="form-help">The preference is visible and remembered. Dates where both readings are valid still ask you to choose.</span>
      </div>
    </div>
    <div class="dialog-actions">
      <button class="skin-button skin-button--wide" id="options-cancel" type="button">Cancel</button>
      <button class="skin-button skin-button--wide" id="options-save" type="button">Apply</button>
    </div>
  </dialog>

  <dialog id="custom-dialog" aria-labelledby="custom-title">
    <h2 class="dialog-title" id="custom-title">Custom format</h2>
    <p class="dialog-copy">Match a date format from your code, spreadsheet, or export. Choose its notation and edit the pattern below.</p>
    <div class="form-grid">
      <div class="form-field">
        <label for="dialect-select">Dialect</label>
        <select id="dialect-select"></select>
      </div>
      <div class="form-field">
        <label for="pattern-input">Pattern</label>
        <input id="pattern-input" autocomplete="off" spellcheck="false">
        <span class="form-help" id="token-help"></span>
      </div>
      <div class="form-field">
        <span class="form-legend">Preview</span>
        <output class="dialog-preview" id="custom-preview"></output>
      </div>
    </div>
    <div class="dialog-actions">
      <button class="skin-button skin-button--wide" id="custom-cancel" type="button">Cancel</button>
      <button class="skin-button skin-button--wide" id="custom-save" type="button">Use format</button>
    </div>
  </dialog>
`;

const elements = Object.freeze({
  primary: document.querySelector('#primary-input'),
  feedback: document.querySelector('#feedback'),
  choicePanel: document.querySelector('#choice-panel'),
  choiceCopy: document.querySelector('#choice-copy'),
  choiceActions: document.querySelector('#choice-actions'),
  zoneName: document.querySelector('#zone-name'),
  zoneClock: document.querySelector('#zone-clock'),
  modeLamp: document.querySelector('#mode-lamp'),
  modeLabel: document.querySelector('#mode-label'),
  now: document.querySelector('#now-button'),
  options: document.querySelector('#options-dialog'),
  optionsOpen: document.querySelector('#options-button'),
  optionsSave: document.querySelector('#options-save'),
  optionsCancel: document.querySelector('#options-cancel'),
  zoneInput: document.querySelector('#zone-input'),
  zoneList: document.querySelector('#zone-list'),
  browserZoneHelp: document.querySelector('#browser-zone-help'),
  orderSelect: document.querySelector('#order-select'),
  custom: document.querySelector('#custom-dialog'),
  customOpen: document.querySelector('#custom-button'),
  customSave: document.querySelector('#custom-save'),
  customCancel: document.querySelector('#custom-cancel'),
  dialectSelect: document.querySelector('#dialect-select'),
  patternInput: document.querySelector('#pattern-input'),
  tokenHelp: document.querySelector('#token-help'),
  customPreview: document.querySelector('#custom-preview'),
});

const inputs = new Map([
  ['primary', elements.primary],
  ...FIELD_DEFINITIONS.map((field) => [field.id, document.querySelector(`#field-${field.id}`)]),
]);

const params = new URLSearchParams(window.location.search);
const storedZone = safeStorageGet(STORAGE.zone);
const detectedBrowserZone = browserTimeZone();
const state = {
  mode: 'live',
  instant: nowInstant(),
  zone: queryTimeZone(params, storedZone),
  dateOrder: queryDateOrder(params),
  fold: ['earlier', 'later'].includes(params.get('fold')) ? params.get('fold') : 'none',
  source: { kind: 'live', raw: '' },
  customDialect: queryDialect(params),
  customPattern: '',
  edit: null,
  initialError: null,
};
state.customPattern = queryPattern(params, state.customDialect);

function commonParseOptions(overrides = {}) {
  return {
    zone: state.zone,
    dateOrder: state.dateOrder,
    referenceInstant: nowInstant(),
    baseInstant: state.instant,
    baseSource: state.source,
    fold: 'reject',
    customDialect: state.customDialect,
    customPattern: state.customPattern,
    ...overrides,
  };
}

function initializeFromLocation() {
  const locationParams = new URLSearchParams(window.location.search);
  if (window.location.pathname === '/' || /^\/*$/.test(window.location.pathname)) {
    state.mode = 'live';
    state.instant = nowInstant();
    state.source = { kind: 'live', raw: formatIsoUtc(state.instant) };
    return;
  }

  try {
    const variants = decodePathVariants(window.location.pathname);
    let lastError;
    for (const [index, variant] of variants.entries()) {
      try {
        const result = parseDateInput(variant, commonParseOptions({
          fold: locationParams.get('fold') || 'reject',
          dialect: locationParams.get('dialect') || state.customDialect,
          pattern: locationParams.get('fmt') || state.customPattern,
          dateOrderConfirmed: locationParams.has('order'),
        }));
        state.mode = 'fixed';
        state.instant = result.instant;
        state.fold = result.fold;
        state.source = cloneSource(result.source);
        if (state.source.kind === 'custom') {
          state.customDialect = state.source.dialect || state.customDialect;
          state.customPattern = state.source.pattern || state.customPattern;
        }
        return;
      } catch (error) {
        lastError = error;
        if (index === variants.length - 1) throw lastError;
      }
    }
  } catch (error) {
    state.mode = 'fixed';
    state.source = {
      kind: 'invalid',
      raw: (() => {
        try {
          return decodePathVariants(window.location.pathname).at(-1) || '';
        } catch {
          return window.location.pathname.slice(1);
        }
      })(),
    };
    state.initialError = error;
  }
}

initializeFromLocation();

function formatField(id, instant = state.instant) {
  switch (id) {
    case 'unix-seconds': return formatUnixSeconds(instant);
    case 'unix-milliseconds': return formatUnixMilliseconds(instant);
    case 'spreadsheet': return formatSpreadsheet(instant, state.zone);
    case 'iso': return formatIsoUtc(instant);
    case 'local': return formatLocalIso(instant, state.zone);
    case 'custom': return formatCustom(instant, state.zone, state.customDialect, state.customPattern);
    default: throw new Error(`Unknown field: ${id}`);
  }
}

function topValue() {
  if (state.mode === 'live') return formatIsoUtc(state.instant);
  if (state.source?.raw) return state.source.raw;
  return formatIsoUtc(state.instant);
}

function setInputValue(id, value, force = false) {
  const input = inputs.get(id);
  if (!input || (!force && document.activeElement === input)) return;
  if (input.value !== value) input.value = value;
}

function markInvalid(id, invalid) {
  const input = inputs.get(id);
  const well = document.querySelector(`#well-${id}`);
  input?.setAttribute('aria-invalid', String(Boolean(invalid)));
  if (well) well.dataset.invalid = String(Boolean(invalid));
}

function clearInvalidStates() {
  for (const id of inputs.keys()) markInvalid(id, false);
}

function setFeedback(message = '', tone = 'neutral') {
  elements.feedback.textContent = message;
  elements.feedback.dataset.tone = tone;
}

function closeChoicePanel() {
  elements.choicePanel.dataset.open = 'false';
  elements.choiceActions.replaceChildren();
  elements.choiceCopy.textContent = '';
}

let summaryZone;
let summaryFormatter;
function renderValues({ force = false } = {}) {
  setInputValue('primary', topValue(), force);
  for (const field of FIELD_DEFINITIONS) {
    try {
      setInputValue(field.id, formatField(field.id), force);
    } catch (error) {
      setInputValue(field.id, `Unavailable — ${error.message}`, force);
    }
  }

  const dialect = DIALECTS[state.customDialect];
  document.querySelector('#meta-custom').textContent = `${dialect.label} · ${state.customPattern}`;
  document.querySelector('#meta-local').textContent = state.zone;
  document.querySelector('#meta-spreadsheet').textContent = 'Excel / Sheets · local time';
  if (summaryZone !== state.zone) {
    summaryZone = state.zone;
    summaryFormatter = new Intl.DateTimeFormat('en', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: state.zone,
    });
  }
  const summary = summaryFormatter.format(new Date(Number(state.instant.epochMilliseconds)));
  const summaryElement = document.querySelector('#date-summary');
  if (summaryElement.textContent !== summary) summaryElement.textContent = summary;
  document.querySelector('#date-order-note').textContent = `${state.dateOrder} dates.`;
  elements.modeLamp.dataset.mode = state.mode;
  elements.modeLabel.textContent = state.mode === 'live' ? 'Live' : 'Fixed';
  document.querySelector('#mode-toggle').setAttribute('aria-pressed', String(state.mode === 'live'));
}

function renderZoneStatus() {
  try {
    const status = getZoneStatus(nowInstant(), state.zone);
    elements.zoneName.textContent = status.timeZone.replaceAll('_', ' ');
    elements.zoneName.title = `Change time zone · UTC${status.offset} · ${status.abbreviation}`;
    elements.zoneClock.textContent = `Current time ${status.time}`;
    elements.zoneClock.dateTime = status.iso;
  } catch (error) {
    elements.zoneName.textContent = state.zone.replaceAll('_', ' ');
    elements.zoneClock.textContent = error.message;
  }
}

function sourceForField(id, raw, result) {
  const base = cloneSource(result.source) || {};
  if (id === 'primary') {
    return {
      ...base,
      raw: base.raw ?? raw,
      fold: result.fold || 'none',
    };
  }
  const specifics = {
    'unix-seconds': { kind: 'unix-seconds' },
    'unix-milliseconds': { kind: 'unix-milliseconds' },
    spreadsheet: { kind: 'spreadsheet', timeZone: state.zone },
    iso: { kind: 'iso' },
    local: { kind: 'local', timeZone: state.zone },
    custom: {
      kind: 'custom',
      timeZone: state.zone,
      dialect: state.customDialect,
      pattern: state.customPattern,
      fmt: state.customPattern,
    },
  }[id] || {};
  return {
    ...base,
    ...specifics,
    raw,
    explicit: id !== 'primary',
    fold: result.fold || 'none',
  };
}

function parseField(id, raw, overrides = {}) {
  const options = commonParseOptions(overrides);
  let result;
  switch (id) {
    case 'primary':
      result = parseDateInput(raw, options);
      break;
    case 'unix-seconds':
      result = parseDateInput(raw, { ...options, explicitType: 'unix-seconds' });
      break;
    case 'unix-milliseconds':
      result = parseDateInput(raw, { ...options, explicitType: 'unix-milliseconds' });
      break;
    case 'spreadsheet':
      result = parseDateInput(raw, { ...options, explicitType: 'spreadsheet' });
      break;
    case 'iso':
    case 'local':
      result = parseDateInput(raw, { ...options, explicitType: 'iso' });
      break;
    case 'custom':
      result = parseDateInput(raw, {
        ...options,
        explicitType: 'custom',
        dialect: state.customDialect,
        pattern: state.customPattern,
      });
      break;
    default:
      throw new Error(`Unknown editor: ${id}`);
  }

  return resultWithSource(result, sourceForField(id, raw, result));
}

function showAmbiguity(error, edit) {
  closeChoicePanel();
  elements.choicePanel.dataset.open = 'true';
  elements.choiceCopy.textContent = error.message;

  if (error.code === 'AMBIGUOUS_CUSTOM_DATE') {
    for (const candidate of error.details?.candidates || []) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'skin-button skin-button--wide';
      button.textContent = candidate.dateOrder;
      button.setAttribute('aria-label', `${candidate.label}: ${candidate.pattern}`);
      button.addEventListener('click', () => resolveEditChoice(edit, {
        selectedPattern: candidate.pattern,
        dateOrder: candidate.dateOrder,
        dateOrderConfirmed: true,
      }));
      elements.choiceActions.append(button);
    }
    return;
  }

  if (error.code === 'AMBIGUOUS_LOCAL_TIME') {
    const alternatives = error.details?.alternatives;
    for (const choice of ['earlier', 'later']) {
      if (!alternatives?.[choice]) continue;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'skin-button skin-button--wide';
      const offset = getZoneStatus(alternatives[choice], state.zone).offset;
      button.textContent = `${choice} · ${offset}`;
      button.addEventListener('click', () => resolveEditChoice(edit, { fold: choice }));
      elements.choiceActions.append(button);
    }
  }
}

function applyValidEdit(edit, result) {
  edit.valid = result;
  state.instant = result.instant;
  state.fold = result.fold || 'none';
  state.source = cloneSource(result.source);
  state.mode = 'fixed';
  markInvalid(edit.id, false);
  closeChoicePanel();
  setFeedback('Valid. Other representations updated; commit with Enter or by leaving the field.');
  renderValues();
}

function resolveEditChoice(edit, overrides) {
  if (!edit || state.edit !== edit) return;
  try {
    const result = parseField(edit.id, edit.input.value, overrides);
    if (overrides.dateOrder) {
      state.dateOrder = overrides.dateOrder;
      elements.orderSelect.value = state.dateOrder;
      safeStorageSet(STORAGE.order, state.dateOrder);
    }
    applyValidEdit(edit, result);
    edit.input.focus();
  } catch (error) {
    handleEditError(edit, error);
  }
}

function handleEditError(edit, error) {
  edit.valid = null;
  markInvalid(edit.id, true);
  setFeedback(error instanceof Error ? error.message : 'That value is not valid.', 'error');
  if (error?.code === 'AMBIGUOUS_CUSTOM_DATE' || error?.code === 'AMBIGUOUS_LOCAL_TIME') {
    showAmbiguity(error, edit);
  } else {
    closeChoicePanel();
  }
}

function beginEdit(id, input) {
  if (state.edit?.input === input) return;
  const wasLive = state.mode === 'live';
  if (wasLive) {
    state.instant = nowInstant();
    state.mode = 'fixed';
    renderValues();
  }
  state.edit = {
    id,
    input,
    wasLive,
    changed: false,
    valid: null,
    initialValue: input.value,
    initialMode: wasLive ? 'live' : state.mode,
    initialInstant: state.instant,
    initialSource: cloneSource(state.source),
    initialFold: state.fold,
  };
  setFeedback('');
}

function updateEdit(edit) {
  edit.changed = edit.input.value !== edit.initialValue;
  if (!edit.changed) {
    edit.valid = null;
    markInvalid(edit.id, false);
    closeChoicePanel();
    return;
  }
  const attempt = tryParseDateInputForField(edit.id, edit.input.value);
  if (attempt.ok) applyValidEdit(edit, attempt.value);
  else handleEditError(edit, attempt.error);
}

function tryParseDateInputForField(id, raw, overrides = {}) {
  try {
    return { ok: true, value: parseField(id, raw, overrides) };
  } catch (error) {
    if (error instanceof DateInputError) return { ok: false, error };
    throw error;
  }
}

function restoreEdit(edit) {
  state.mode = edit.initialMode;
  state.instant = edit.initialInstant;
  state.source = edit.initialSource;
  state.fold = edit.initialFold;
  state.edit = null;
  clearInvalidStates();
  closeChoicePanel();
  setFeedback('');
  renderValues({ force: true });
}

function commitHistory({ replace = false, forceExplicit = false } = {}) {
  const relative = relativeUrlForSource(state.source, state, { forceExplicit });
  const method = replace ? 'replaceState' : 'pushState';
  window.history[method]({ source: state.source?.kind }, '', relative);
}

function commitEdit(edit, { blur = false } = {}) {
  if (!edit || state.edit !== edit) return;
  if (!edit.changed) {
    if (edit.wasLive) {
      state.mode = 'live';
      state.source = { kind: 'live', raw: '' };
    }
    state.edit = null;
    clearInvalidStates();
    closeChoicePanel();
    setFeedback('');
    renderValues({ force: true });
    return;
  }
  if (!edit.valid) {
    if (!blur) setFeedback('Resolve the value before committing, or press Escape to restore it.', 'error');
    return;
  }

  state.mode = 'fixed';
  state.source = cloneSource(edit.valid.source);
  state.instant = edit.valid.instant;
  state.fold = edit.valid.fold || 'none';
  state.edit = null;
  clearInvalidStates();
  closeChoicePanel();
  commitHistory({ forceExplicit: edit.id !== 'primary' });
  setFeedback('Date committed. The address now reproduces this representation.', 'success');
  renderValues({ force: true });
}

for (const [id, input] of inputs) {
  input.addEventListener('focus', () => beginEdit(id, input));
  input.addEventListener('input', () => {
    if (!state.edit || state.edit.input !== input) beginEdit(id, input);
    updateEdit(state.edit);
  });
  input.addEventListener('keydown', (event) => {
    const edit = state.edit;
    if (!edit || edit.input !== input) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      restoreEdit(edit);
      input.blur();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commitEdit(edit);
      if (!state.edit) input.blur();
    }
  });
  input.addEventListener('blur', () => {
    const edit = state.edit;
    if (!edit || edit.input !== input) return;
    window.setTimeout(() => {
      if (state.edit === edit && !elements.choicePanel.contains(document.activeElement)) {
        commitEdit(edit, { blur: true });
      }
    }, 0);
  });
}

async function copyText(value, message) {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    const fallback = document.createElement('textarea');
    fallback.value = value;
    fallback.setAttribute('readonly', '');
    fallback.className = 'visually-hidden';
    document.body.append(fallback);
    fallback.select();
    const copied = document.execCommand('copy');
    fallback.remove();
    if (!copied) throw new Error('Clipboard access was denied.');
  }
  setFeedback(message, 'success');
}

function rowSource(id) {
  if (id === 'primary') {
    if (state.mode === 'live') return { kind: 'live', raw: '' };
    return cloneSource(state.source);
  }
  const raw = formatField(id);
  return sourceForField(id, raw, { fold: state.fold, source: {} });
}

for (const button of document.querySelectorAll('[data-copy]')) {
  button.addEventListener('click', () => {
    const id = button.dataset.copy;
    const value = id === 'primary' ? topValue() : formatField(id);
    void copyText(value, `${FIELD_DEFINITIONS.find((field) => field.id === id)?.label || 'Primary value'} copied.`);
  });
}

for (const button of document.querySelectorAll('[data-link]')) {
  button.addEventListener('click', () => {
    const id = button.dataset.link;
    const link = id === 'primary'
      ? absoluteUrlForSource(rowSource(id), state)
      : buildRowLink({
          origin: window.location.origin,
          instant: state.instant,
          rowKind: id,
          zone: state.zone,
          dateOrder: state.dateOrder,
          fold: state.fold,
          dialect: state.customDialect,
          pattern: state.customPattern,
        });
    void copyText(link, 'Format-specific link copied.');
  });
}

elements.now.addEventListener('click', () => {
  state.mode = 'live';
  state.instant = nowInstant();
  state.source = { kind: 'live', raw: '' };
  state.fold = 'none';
  state.edit = null;
  clearInvalidStates();
  closeChoicePanel();
  window.history.pushState({ source: 'live' }, '', '/');
  setFeedback('');
  renderValues({ force: true });
});

document.querySelector('#mode-toggle').addEventListener('click', () => {
  // An unchanged editor represents a temporarily paused live clock.
  const wasLive = state.mode === 'live' || (state.edit?.wasLive && !state.edit.changed);
  if (!wasLive) {
    elements.now.click();
    return;
  }
  state.edit = null;
  state.mode = 'fixed';
  state.instant = nowInstant();
  state.source = { kind: 'iso', raw: formatIsoUtc(state.instant) };
  state.fold = 'none';
  clearInvalidStates();
  closeChoicePanel();
  setFeedback('');
  commitHistory();
  renderValues({ force: true });
});

function availableZones() {
  try {
    const supported = Intl.supportedValuesOf('timeZone');
    return ['UTC', ...supported.filter((zone) => zone !== 'UTC')];
  } catch {
    return [...FALLBACK_ZONES];
  }
}

const zoneFragment = document.createDocumentFragment();
for (const zone of availableZones()) {
  const option = document.createElement('option');
  option.value = zone;
  zoneFragment.append(option);
}
elements.zoneList.append(zoneFragment);
elements.browserZoneHelp.textContent = `Browser zone: ${detectedBrowserZone}`;

elements.optionsOpen.addEventListener('click', () => {
  elements.zoneInput.value = state.zone;
  elements.orderSelect.value = state.dateOrder;
  elements.options.showModal();
  elements.zoneInput.focus();
});
elements.zoneName.addEventListener('click', () => elements.optionsOpen.click());
elements.optionsCancel.addEventListener('click', () => elements.options.close());
elements.optionsSave.addEventListener('click', () => {
  try {
    const previousZone = state.zone;
    const nextZone = validateTimeZone(elements.zoneInput.value);
    const nextOrder = elements.orderSelect.value === 'DMY' ? 'DMY' : 'MDY';
    state.zone = nextZone;
    state.dateOrder = nextOrder;
    if (sourceNeedsZone(state.source) && previousZone !== nextZone) {
      const raw = formatSourceInstant(state.source, state.instant, nextZone);
      state.source = { ...state.source, raw, timeZone: nextZone };
    }
    safeStorageSet(STORAGE.zone, state.zone);
    safeStorageSet(STORAGE.order, state.dateOrder);
    elements.options.close();
    if (state.mode === 'fixed') commitHistory();
    else window.history.pushState({ source: 'live' }, '', `/?tz=${encodeURIComponent(state.zone)}`);
    setFeedback(`Time zone set to ${state.zone}; the instant was preserved.`, 'success');
    renderValues({ force: true });
    renderZoneStatus();
  } catch (error) {
    setFeedback(error.message, 'error');
    elements.zoneInput.focus();
  }
});

for (const dialect of Object.values(DIALECTS)) {
  const option = document.createElement('option');
  option.value = dialect.id;
  option.textContent = dialect.label;
  elements.dialectSelect.append(option);
}

function updateCustomDialog({ useDialectDefault = false } = {}) {
  const dialect = DIALECTS[elements.dialectSelect.value];
  if (useDialectDefault) elements.patternInput.value = dialect.examplePattern;
  elements.tokenHelp.textContent = `Tokens: ${dialect.tokens.join(' · ')}. ${dialect.literalHelp}`;
  try {
    elements.customPreview.value = formatCustom(
      state.instant,
      state.zone,
      dialect.id,
      elements.patternInput.value,
    );
    elements.customPreview.textContent = elements.customPreview.value;
    elements.customSave.disabled = false;
  } catch (error) {
    elements.customPreview.value = error.message;
    elements.customPreview.textContent = error.message;
    elements.customSave.disabled = true;
  }
}

elements.customOpen.addEventListener('click', () => {
  elements.dialectSelect.value = state.customDialect;
  elements.patternInput.value = state.customPattern;
  updateCustomDialog();
  elements.custom.showModal();
  elements.patternInput.focus();
});
elements.customCancel.addEventListener('click', () => elements.custom.close());
elements.dialectSelect.addEventListener('change', () => updateCustomDialog({ useDialectDefault: true }));
elements.patternInput.addEventListener('input', () => updateCustomDialog());
elements.customSave.addEventListener('click', () => {
  const dialect = elements.dialectSelect.value;
  const pattern = elements.patternInput.value;
  try {
    formatCustom(state.instant, state.zone, dialect, pattern);
    state.customDialect = dialect;
    state.customPattern = pattern;
    safeStorageSet(STORAGE.dialect, dialect);
    safeStorageSet(STORAGE.pattern, pattern);
    if (state.source?.kind === 'custom') {
      state.source = {
        ...state.source,
        dialect,
        pattern,
        fmt: pattern,
        raw: formatCustom(state.instant, state.zone, dialect, pattern),
      };
      commitHistory();
    }
    elements.custom.close();
    setFeedback('Custom format updated.', 'success');
    renderValues({ force: true });
  } catch (error) {
    setFeedback(error.message, 'error');
  }
});

window.addEventListener('popstate', () => {
  const freshParams = new URLSearchParams(window.location.search);
  state.zone = queryTimeZone(freshParams, safeStorageGet(STORAGE.zone));
  state.dateOrder = queryDateOrder(freshParams);
  state.customDialect = queryDialect(freshParams);
  state.customPattern = queryPattern(freshParams, state.customDialect);
  state.fold = ['earlier', 'later'].includes(freshParams.get('fold')) ? freshParams.get('fold') : 'none';
  state.edit = null;
  state.initialError = null;
  initializeFromLocation();
  clearInvalidStates();
  closeChoicePanel();
  setFeedback('');
  renderValues({ force: true });
});

let lastTick = 0;
function tick(frameTime) {
  if (frameTime - lastTick >= 32) {
    lastTick = frameTime;
    if (state.mode === 'live' && !state.edit) {
      state.instant = nowInstant();
      state.source.raw = formatIsoUtc(state.instant);
      renderValues();
    }
    renderZoneStatus();
  }
  requestAnimationFrame(tick);
}

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) {
    if (state.mode === 'live' && !state.edit) state.instant = nowInstant();
    renderValues();
    renderZoneStatus();
  }
});

renderValues({ force: true });
renderZoneStatus();
requestAnimationFrame(tick);

if (state.initialError) {
  markInvalid('primary', true);
  setFeedback(state.initialError.message || 'The date in this address is not valid.', 'error');
  const syntheticEdit = {
    id: 'primary',
    input: elements.primary,
    changed: true,
    valid: null,
    initialValue: elements.primary.value,
    initialMode: 'fixed',
    initialInstant: state.instant,
    initialSource: cloneSource(state.source),
    initialFold: state.fold,
  };
  state.edit = syntheticEdit;
  if (
    state.initialError.code === 'AMBIGUOUS_CUSTOM_DATE' ||
    state.initialError.code === 'AMBIGUOUS_LOCAL_TIME'
  ) {
    showAmbiguity(state.initialError, syntheticEdit);
  }
}

// A tiny testing seam avoids exposing mutable state to normal application code.
if (import.meta.env.DEV) {
  window.__TIME_BRENT_APP__ = Object.freeze({
    getState: () => ({ ...state, source: cloneSource(state.source), edit: undefined }),
    parseDateInput,
    parseIsoInstant,
  });
}
